/**
 * useRoomDiscoveryReport — counts of room.claimed audit events grouped
 * by building over a recent window. Lets supervisors / contractor_admins
 * see how the cleaner-driven inventory is filling in week over week.
 *
 * Two modes:
 *   - Active-org (default): client-side query of audit_logs; RLS gates
 *     by the active org from JWT. Simple, no Edge Function round trip.
 *   - All-orgs (toggle on Reports page): invokes the
 *     report-discovery-multi-org Edge Function which queries the union
 *     of every org the user is a member of and returns rows tagged
 *     with organization_id. Aggregator returns per-(org, building) rows.
 *
 * Hook is enabled only for super_admin / contractor_admin (matches
 * the audit_logs RLS gate).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useRole }                        from './useAuth'
import { AppError }                       from '@/lib/errors/AppError'
import {
  aggregateRoomDiscovery,
  aggregateRoomDiscoveryMultiOrg,
  type AuditClaimRow,
  type BuildingRow,
  type OrgRow,
  type RoomDiscoveryReport,
  type RoomDiscoveryMultiOrgReport,
} from './aggregateRoomDiscovery'

export type {
  RoomDiscoveryReport,
  RoomDiscoveryMultiOrgReport,
  RoomDiscoveryByBuildingRow,
  RoomDiscoveryByOrgAndBuildingRow,
} from './aggregateRoomDiscovery'

const DEFAULT_RANGE_DAYS = 7
const SELECT_LIMIT       = 5000

export function useRoomDiscoveryReport(rangeDays: number = DEFAULT_RANGE_DAYS): UseQueryResult<RoomDiscoveryReport> {
  const supabase = useSupabase()
  const role     = useRole()

  return useQuery({
    queryKey: ['room-discovery-report', rangeDays, role],
    staleTime: 60_000,
    enabled:   role === 'super_admin' || role === 'contractor_admin',
    queryFn: async (): Promise<RoomDiscoveryReport> => {
      const cutoffIso = new Date(Date.now() - rangeDays * 24 * 60 * 60 * 1000).toISOString()

      const { data: claimRows, error: claimErr } = await supabase
        .from('audit_logs')
        .select('metadata_json, entity_id, created_at')
        .eq('action_type', 'room.claimed')
        .gte('created_at', cutoffIso)
        .order('created_at', { ascending: false })
        .limit(SELECT_LIMIT)
      if (claimErr) {
        throw new AppError({
          code:        'REPORT_FETCH_FAILED',
          message:     claimErr.message,
          userMessage: 'Could not load room discovery activity.',
          status:      500,
          cause:       claimErr,
        })
      }

      const { data: buildingRows, error: bErr } = await supabase
        .from('buildings')
        .select('id, name')
        .eq('is_active', true)
      if (bErr) {
        throw new AppError({
          code:        'REPORT_FETCH_FAILED',
          message:     bErr.message,
          userMessage: 'Could not load room discovery activity.',
          status:      500,
          cause:       bErr,
        })
      }

      return aggregateRoomDiscovery(
        rangeDays,
        (claimRows ?? []) as unknown as AuditClaimRow[],
        (buildingRows ?? []) as unknown as BuildingRow[],
      )
    },
  })
}

interface MultiOrgFnResponse {
  range_days:    number
  organizations: OrgRow[]
  claim_rows:    AuditClaimRow[]
  buildings:     BuildingRow[]
}

/**
 * Multi-org variant: invokes report-discovery-multi-org Edge Function
 * (which uses service_role to read across the user's memberships) and
 * runs the multi-org aggregator. Same role gate (admin-only).
 *
 * `enabled` lets the caller defer the network round trip until the
 * toggle is actually on — single-org admins (and any admin who hasn't
 * flipped "All my orgs") never hit the Edge Function or burn rate-limit
 * budget on page load.
 */
export function useRoomDiscoveryReportAllOrgs(
  rangeDays: number = DEFAULT_RANGE_DAYS,
  options:   { enabled?: boolean } = {},
): UseQueryResult<RoomDiscoveryMultiOrgReport> {
  const supabase = useSupabase()
  const role     = useRole()
  const callerEnabled = options.enabled ?? true

  return useQuery({
    queryKey: ['room-discovery-report-all-orgs', rangeDays, role],
    staleTime: 60_000,
    enabled:   callerEnabled && (role === 'super_admin' || role === 'contractor_admin'),
    queryFn: async (): Promise<RoomDiscoveryMultiOrgReport> => {
      const { data, error } = await supabase.functions.invoke<MultiOrgFnResponse>('report-discovery-multi-org', {
        body: { range_days: rangeDays },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { detail?: string }
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code:        'REPORT_FETCH_FAILED',
          message:     detail,
          userMessage: 'Could not load multi-org discovery activity.',
          status:      500,
          cause:       error,
        })
      }
      if (!data) {
        throw new AppError({
          code:        'EMPTY_RESPONSE',
          message:     'No data',
          userMessage: 'The server returned no result.',
          status:      500,
        })
      }
      return aggregateRoomDiscoveryMultiOrg(
        data.range_days,
        data.claim_rows,
        data.buildings,
        data.organizations,
      )
    },
  })
}
